<main class="homepage-shell">
  <section class="homepage-hero" :style="{ backgroundImage: heroBackground }">
    <div class="homepage-hero__shade"></div>
    <div class="homepage-container homepage-hero__content">
      <div class="homepage-hero__copy">
        <h1>
          <span>{{ $t('home.heroTitle') }}</span>
          <span>{{ $t('home.heroSubtitle') }}</span>
        </h1>
        <p>{{ $t('home.heroDescription') }}</p>
        <div class="homepage-hero__actions">
          <router-link :to="{ name: 'AllClubs' }" class="homepage-btn homepage-btn--primary">{{ $t('home.findClub') }}</router-link>
          <router-link :to="{ name: 'About', hash: '#contacts' }" class="homepage-btn homepage-btn--ghost">
            {{ $t('home.organizeTournament') }}
          </router-link>
        </div>
      </div>

      <div class="homepage-stats" :aria-label="$t('home.serviceStats')">
        <div v-for="stat in localizedStats" :key="stat.key" class="homepage-stat">
          <i :class="stat.icon"></i>
          <strong>{{ stat.value }}</strong>
          <span>{{ stat.label }}</span>
        </div>
      </div>
    </div>
  </section>

  <section id="home-search" class="homepage-search-section">
    <div class="homepage-container">
      <PublicSearchToolbar
        :model-value="searchQuery"
        :placeholder="searchPlaceholder"
        :aria-label="searchAriaLabel"
        :hints="searchHints"
        :results="visibleSearchResults"
        :results-count="submittedSearchResultsCount"
        :more-route="searchPageRoute"
        :show-results-panel="isSearchDropdownVisible"
        show-hints
        show-dropdown-results
        @update:model-value="updateSearchQuery"
        @submit="submitSearch"
      />
    </div>
  </section>

  <section class="homepage-section homepage-section--events">
    <div class="homepage-container">
      <div class="homepage-section__head">
        <h2>{{ $t('home.upcomingTitle') }}</h2>
        <router-link :to="{ name: 'AllClubs' }" class="app-link-button">{{ $t('home.allTournaments') }} <i class="bi bi-arrow-right"></i></router-link>
      </div>

      <PublicCarousel :items="events" :label="$t('home.upcomingTitle')" :previous-label="$t('home.previousTournaments')" :next-label="$t('home.nextTournaments')">
        <template #item="{ item: event }">
          <PublicTournamentCard
            :to="event.route"
            :title="event.title"
            :badge="event.badge"
            :time-badge="event.time"
            :system-label="event.system"
            :system-type="event.systemType"
            :system-class="getSystemBadgeClass(event.systemType)"
            :background-image="getEventBackground(event)"
            class="homepage-event-card"
            image-mode
            scroll-card
          >
            <template #description>
              <p>{{ [event.club, event.city].filter(Boolean).join(' · ') }}<template v-if="event.place?.name"> · <span class="text-nowrap"><i class="bi bi-geo-alt" aria-hidden="true"></i>{{ event.place.name }}</span></template></p>
            </template>
            <template #system-meta>
              <span
                class="homepage-event-card__system-meta-badge"
                :style="{ '--homepage-system-meta-color': event.competitionFormatColor }"
              >
                <span
                  class="icon-inline"
                  tabindex="0"
                  v-tooltip.bottom="{ value: event.competitionFormatLabel || $t('home.competitionIndividual'), class: 'tt-sm', disabled: false }"
                >
                  <i :class="event.competitionFormatIcon"></i>
                </span>
              </span>
              <span
                class="homepage-event-card__system-meta-badge"
                :style="{ '--homepage-system-meta-color': event.ratedColor }"
              >
                <span
                  class="icon-inline"
                  tabindex="0"
                  v-tooltip.bottom="{ value: event.ratedLabel || $t('home.friendly'), class: 'tt-sm', disabled: false }"
                >
                  <i :class="event.ratedIcon"></i>
                </span>
              </span>
            </template>

            <template #meta>
              <span class="homepage-event-card__game-meta">
                <span>
                  <IconChessVariant
                    :variant="event.chessVariant"
                    height="12px"
                    :width="getEventChessVariantWidth(event)"
                    color="var(--home-gold-strong)"
                  />
                  {{ event.chessVariantLabel }}
                </span>
                <span>
                  <component
                    :is="event.controlIconComponent"
                    v-if="event.controlIconComponent"
                    class="homepage-event-card__control-icon"
                    :size="getEventControlIconSize(event)"
                    color="var(--home-gold-strong)"
                  />
                  <i v-else :class="event.icon"></i>
                  {{ event.control }}
                </span>
              </span>
            </template>

            <template #extra>
              <template v-if="event.registrationText">
                <span class="homepage-event-card__onsite">
                  <i class="bi bi-clipboard-check"></i>{{ event.registrationText }}
                </span>
              </template>
              <template v-else>
                <div class="homepage-event-card__capacity">
                  <span><i class="bi bi-clipboard-check"></i>{{ event.players }}</span>
                  <div class="homepage-event-card__progress"><i :style="{ width: event.progress }"></i></div>
                </div>
              </template>
            </template>
          </PublicTournamentCard>
        </template>
        <template v-if="events.length < 4" #after>
          <article class="homepage-event-card homepage-event-card--placeholder">
            <i class="bi bi-calendar-plus"></i>
            <h3>{{ $t('home.tournamentPlaceholderTitle') }}</h3>
            <p>{{ $t('home.tournamentPlaceholderText') }}</p>
          </article>
        </template>
      </PublicCarousel>
    </div>
  </section>

  <section class="homepage-section">
    <div class="homepage-container">
      <div class="homepage-section__head homepage-clubs-intro">
        <h2>{{ $t('home.clubsTitle') }}</h2>
        <router-link :to="{ name: 'AllClubs' }" class="app-link-button">{{ $t('home.allClubs') }} <i class="bi bi-arrow-right"></i></router-link>
      </div>

      <PublicCarousel :items="clubs" compact :label="$t('home.clubsTitle')" :previous-label="$t('home.previousClubs')" :next-label="$t('home.nextClubs')">
        <template #item="{ item: club }">
          <PublicClubCard
            :club="club"
            class="public-hover-card public-hover-card--scroll"
          />
        </template>
        <template #after>
          <article class="homepage-club-card homepage-club-card--placeholder">
            <div class="homepage-club-card__placeholder-icon">
              <i class="bi bi-plus-lg"></i>
            </div>
            <h3>{{ $t('home.clubPlaceholderTitle') }}</h3>
            <p>{{ $t('home.clubPlaceholderText') }}</p>
            <router-link :to="{ name: 'About', hash: '#contacts' }" class="homepage-btn homepage-btn--primary">
              {{ $t('home.createClub') }}
            </router-link>
          </article>
        </template>
      </PublicCarousel>
    </div>
  </section>

  <section class="homepage-section">
    <div class="homepage-container">
      <div class="homepage-section__head">
        <h2>{{ $t('home.platformTitle') }}</h2>
        <router-link :to="{ name: 'Features' }" class="app-link-button">{{ $t('home.more') }} <i class="bi bi-arrow-right"></i></router-link>
      </div>
      <div class="homepage-feature-grid">
        <div v-for="feature in localizedFeatures" :key="feature.translationKey" class="homepage-feature">
          <i :class="feature.icon"></i>
          <span>{{ feature.title }}</span>
        </div>
        <div class="homepage-feature homepage-feature--mobile-extra" aria-hidden="true">
          <i class="bi bi-plus-lg"></i>
          <span>{{ $t('home.newFeatures') }}</span>
        </div>
      </div>
    </div>
  </section>

  <section class="homepage-section">
    <div class="homepage-container">
      <div class="homepage-section__head">
        <h2>{{ $t('home.latestResults') }}</h2>
      </div>
      <div class="homepage-results-grid">
        <article class="homepage-result-list">
          <h3>{{ $t('home.winners') }}</h3>
          <div
            v-for="result in results"
            :key="`${result.tournamentId || result.event}-${result.playerId || result.name}`"
            class="homepage-result-row"
            :class="{ 'homepage-result-row--group': result.members?.length > 1 }"
          >
              <div class="homepage-result-row__avatar">
                <PublicPlayerAvatar
                  :name="result.name"
                  :avatar-url="result.avatar"
                  :seed="result.playerId || result.name"
                />
              </div>
            <strong class="homepage-result-row__date">{{ result.date }}</strong>
            <div>
              <span v-if="result.members?.length > 1" class="homepage-result-row__name homepage-result-row__name--group">
                <template v-for="(member, index) in result.members" :key="member.playerId || member.participantId || member.name">
                  <span v-if="index > 0" class="homepage-result-row__member-separator">,</span>
                  <span class="homepage-result-row__member">
                    <span class="homepage-result-row__place-icon homepage-result-row__place-icon--1" role="img" :aria-label="$t('home.place', { place: 1 })" :title="$t('home.place', { place: 1 })"></span>
                    <PlayerName :name="member.name" :title="member.title" />
                    <b
                      v-if="member.rating !== null && member.rating !== undefined"
                      v-tooltip.bottom="{ value: $t('home.playerRating'), class: 'tt-sm' }"
                    >{{ formatRating(member.rating, member.provisional) }}</b>
                    <b
                      v-if="member.score"
                      class="homepage-result-row__score-badge"
                      v-tooltip.bottom="{ value: $t('home.tournamentResult'), class: 'tt-sm' }"
                    >{{ member.score }}</b>
                  </span>
                </template>
              </span>
              <span v-else class="homepage-result-row__name">
                <span class="homepage-result-row__place-icon homepage-result-row__place-icon--1" role="img" :aria-label="$t('home.place', { place: 1 })" :title="$t('home.place', { place: 1 })"></span>
                <PlayerName :name="result.name" :title="result.title" />
                <b
                  v-if="result.rating !== null && result.rating !== undefined"
                  v-tooltip.bottom="{ value: $t('home.playerRating'), class: 'tt-sm' }"
                >{{ formatRating(result.rating, result.provisional) }}</b>
              </span>
              <router-link
                v-if="result.route"
                :to="result.route"
                class="homepage-result-row__event homepage-inline-link"
              >
                {{ result.event }}
              </router-link>
              <small v-else class="homepage-result-row__event">{{ result.event }}</small>
            </div>
            <div v-if="!result.members || result.members.length <= 1" class="homepage-result-row__score">
              <b v-tooltip.bottom="{ value: $t('home.tournamentResult'), class: 'tt-sm' }">{{ result.score }}</b>
              <div>
                <em
                  :class="getDeltaClass(result)"
                  v-tooltip.bottom="{ value: $t('home.ratingChange'), class: 'tt-sm' }"
                >
                  {{ result.delta }}
                </em>
                <small
                  class="homepage-result-row__performance"
                  v-tooltip.bottom="{ value: $t('home.tournamentPerformance'), class: 'tt-sm' }"
                >
                  <i class="bi bi-activity"></i>
                  {{ result.performance }}
                </small>
              </div>
            </div>
            <div v-if="result.prizeWinners?.length" class="homepage-result-row__prize-winners">
              <span
                v-for="player in result.prizeWinners"
                :key="`${player.place}-${player.participantId || player.playerId || player.name}`"
                class="homepage-result-row__prize-winner"
              >
                <span
                  class="homepage-result-row__place-icon"
                  :class="`homepage-result-row__place-icon--${player.place}`"
                  role="img"
                  :aria-label="$t('home.place', { place: player.place })"
                  :title="$t('home.place', { place: player.place })"
                ></span>
                <PlayerName :name="player.name" :title="player.title" />
                <b v-if="player.rating !== null && player.rating !== undefined">{{ formatRating(player.rating, player.provisional) }}</b>
              </span>
            </div>
            <div class="homepage-result-row__meta">
              <span>
                <span class="homepage-result-row__meta-icon">
                  <component
                    :is="getResultControlIconComponent(result)"
                    v-if="getResultControlIconComponent(result)"
                    :size="getResultControlIconSize(result)"
                    color="var(--homepage-result-icon-color)"
                    :class="getResultControlIconClass(result)"
                  />
                </span>
                {{ result.control }}
              </span>
              <span>
                <span class="homepage-result-row__meta-icon">
                  <IconChessVariant
                    :variant="result.chessVariant"
                    size="14px"
                    color="var(--homepage-result-icon-color)"
                  />
                </span>
                {{ getResultChessVariantLabel(result) }}
              </span>
            </div>
          </div>
        </article>

        <article class="homepage-panel">
          <h3>{{ $t('home.ratingChanges') }}</h3>
          <div v-for="mover in movers" :key="`${mover.tournamentId || mover.event || mover.club}-${mover.playerId || mover.name}`" class="homepage-rating-row">
            <span class="homepage-rating-row__place">{{ mover.place }}</span>
            <strong class="homepage-rating-row__player">
              <PlayerName :name="mover.name" :title="mover.title" />
            </strong>
            <router-link
              v-if="mover.clubRoute"
              :to="mover.clubRoute"
              class="homepage-rating-row__club homepage-inline-link"
            >
              {{ mover.club }}
            </router-link>
            <small v-else class="homepage-rating-row__club">{{ mover.club }}</small>
            <div class="homepage-rating-row__icons">
              <span>
                <component
                  :is="getResultControlIconComponent(mover)"
                  v-if="getResultControlIconComponent(mover)"
                  :size="getResultControlIconSize(mover)"
                  color="var(--homepage-result-icon-color)"
                  :class="getResultControlIconClass(mover)"
                />
              </span>
              <span>
                <IconChessVariant
                  :variant="mover.chessVariant"
                  size="14px"
                  color="var(--homepage-result-icon-color)"
                />
              </span>
            </div>
            <b class="homepage-rating-row__rating">{{ formatRating(mover.rating, mover.provisional) }}</b>
            <router-link
              v-if="mover.route"
              :to="mover.route"
              class="homepage-rating-row__delta homepage-inline-link"
              :class="getDeltaClass(mover)"
            >
              {{ mover.delta }}
            </router-link>
            <em v-else class="homepage-rating-row__delta" :class="getDeltaClass(mover)">{{ mover.delta }}</em>
          </div>
        </article>

        <article class="homepage-panel">
          <h3>{{ $t('home.topPerformances') }}</h3>
          <div v-for="(performer, performerIndex) in performers" :key="`${performer.tournamentId || performer.event}-${performer.playerId || performer.name}`" class="homepage-performer">
            <span
              v-if="performerIndex < 3"
              class="homepage-performer__place icon-places"
              :class="`icon-place-${performerIndex + 1}`"
            ></span>
            <span v-else class="homepage-performer__place homepage-performer__place--number">
              {{ performerIndex + 1 }}
            </span>
            <PublicPlayerAvatar
              :name="performer.name"
              :avatar-url="performer.avatar"
              :seed="performer.playerId || performer.name"
            />
            <div class="homepage-performer__details">
              <strong class="homepage-performer__name">
                <PlayerName :name="performer.name" :title="performer.title" />
                <b
                  v-tooltip.bottom="{ value: $t('home.playerRating'), class: 'tt-sm' }"
                >
                  {{ formatRating(performer.rating, performer.provisional) }}
                </b>
              </strong>
            </div>
            <component
              :is="performer.route ? 'router-link' : 'div'"
              :to="performer.route"
              class="homepage-performer__score"
              :class="{ 'homepage-inline-link': performer.route }"
            >
              <span class="homepage-performer__variant">
                <IconChessVariant
                  :variant="performer.chessVariant"
                  size="14px"
                  color="var(--homepage-result-icon-color)"
                />
              </span>
              <b>{{ performer.performance }}</b>
            </component>
            <span class="homepage-performer__club">
              <template v-if="performer.date">
                <router-link
                  v-if="performer.route"
                  :to="performer.route"
                  class="homepage-performer__meta-link homepage-inline-link"
                >
                  {{ performer.date }}
                </router-link>
                <span v-else>{{ performer.date }}</span>
                <span> · </span>
              </template>
              <router-link
                v-if="performer.clubRoute"
                :to="performer.clubRoute"
                class="homepage-performer__meta-link homepage-inline-link"
              >
                {{ performer.club }}
              </router-link>
              <span v-else>{{ performer.club }}</span>
            </span>
          </div>
        </article>
      </div>
    </div>
  </section>

  <section class="homepage-section">
    <div class="homepage-container">
      <PublicClubCta />
    </div>
  </section>

  <section class="homepage-section homepage-section--map">
    <div class="homepage-container">
      <h2>{{ $t('home.geographyTitle') }}</h2>
      <ClubGeographyMap />
    </div>
  </section>
</main>
